/* ===========================================================
   Didier's Patisserie — catalogue model (ranges + products)

   Ranges map to catalogue categories; products are fed from the
   catalogue (Trac-IT → VPS ingest) in production, exactly like the
   catalogue PWA. This module seeds the two ranges that were scraped
   for the design; the remaining ranges render as "from the catalogue"
   placeholders until the live ingest is wired in.
   =========================================================== */

import { mediaUrl } from './content';

export type RangeKind = 'order' | 'portfolio';

export interface Range {
  /** Stable key, namespaced by kind: `${kind}:${slug}`. */
  key: string;
  /** URL slug (the part after the kind). */
  slug: string;
  label: string;
  kind: RangeKind;
  /** Website category id (Category5_WebID) — set when fed from the live catalogue. */
  webId?: number | null;
  lede?: string;
  sub?: string;
}

export interface Product {
  code: string;
  name: string;
  rangeKey: string;
  /** Display image (absolute URL). In prod: a VPS-served photo. */
  image: string;
  // ---- catalogue-fed fields (populated from the VPS snapshot later) ----
  description?: string;
  subtitle?: string;
  format?: string;
  allergens?: string[];
  suitability?: string[];
  storage?: string;
  shelfLife?: string;
  packSize?: string;
  /** HTML ingredient declaration (allergens in <strong>). From WebsiteExport_Qry. */
  ingredients?: string;
  /** Defrost guidance (item_webdefrost). */
  defrost?: string;
  /** Reheat guidance (item_webreheat). */
  reheat?: string;
}

export const rangeKey = (kind: RangeKind, slug: string): string => `${kind}:${slug}`;

/**
 * Ranges shown in the header. "Available to Order" = order ranges,
 * "Portfolio" = portfolio ranges. Mirrors the Claude design nav.
 */
export const RANGES: Range[] = [
  {
    key: 'order:afternoon-tea',
    slug: 'afternoon-tea',
    label: 'Afternoon Tea',
    kind: 'order',
    lede: 'Our Afternoon Tea range available to order now.',
    sub: 'We treat each one with the same attention to detail. We never compromise on quality or flavour.',
  },
  { key: 'order:individual-dessert', slug: 'individual-dessert', label: 'Individual Dessert', kind: 'order' },
  { key: 'order:ice-cream', slug: 'ice-cream', label: 'Ice Cream', kind: 'order' },
  {
    key: 'portfolio:individual-patisserie',
    slug: 'individual-patisserie',
    label: 'Individual Patisserie',
    kind: 'portfolio',
    lede: 'Please see below for our individual patisserie in production.',
    sub: 'We treat each one with the same attention to detail. We never compromise on quality or flavour.',
  },
  { key: 'portfolio:potted-dessert', slug: 'potted-dessert', label: 'Potted Dessert', kind: 'portfolio' },
  { key: 'portfolio:afternoon-tea', slug: 'afternoon-tea', label: 'Afternoon Tea', kind: 'portfolio' },
  { key: 'portfolio:sharing-dessert', slug: 'sharing-dessert', label: 'Sharing Dessert', kind: 'portfolio' },
];

/** Raw [name, mediaPath] pairs scraped for the design. */
const SEED_ITEMS: Record<string, [string, string][]> = {
  'order:afternoon-tea': [
    ['Mini Strawberry & Darjeeling Eclair', 'media/185-dp198-mini-strawberry-darjeeling-eclair-sm.jpg'],
    ['Raspberry Vanilla Battenberg', 'media/185-dp206-raspberry-vanilla-battenberg-sm.jpg'],
    ['Lemon Poppy Seed Battenberg', 'media/186-dp020-lemon-poppy-seed-battenberg-sm.jpg'],
    ['Mini Salted Caramel Vanilla Tart', 'media/186-dp183-mini-salted-caramel-vanilla-tart-sm.jpg'],
    ['Mini Passion Fruit \u00c9clair', 'media/186-dp042-mini-passion-fruit-eclair-sm.jpg'],
    ['Mini Lemon Meringue Tart', 'media/186-dp088-mini-lemon-meringue-tart-sm.jpg'],
    ['Mini Sticky Toffee Slice', 'media/186-dp078-mini-sticky-toffee-slice-sm.jpg'],
    ['Mini Croustillant', 'media/186-dp035-mini-croustillant-sm.jpg'],
    ['Mini Carrot Cake Slice', 'media/186-dp036-mini-carrot-cake-slice-sm.jpg'],
    ['Chocolate Orange Battenberg', 'media/186-dp011-chocolate-orange-battenberg-sm.jpg'],
  ],
  'portfolio:individual-patisserie': [
    ['Chocolate Hazelnut Slice 9x3cm', 'media/186-dp016-chocolate-hazelnut-slice-9x3cm-sm.jpg'],
    ['Chocolate Salted Caramel Tart 70mm', 'media/186-dp188-chocolate-salted-caramel-tart-70mm-sm.jpg'],
    ['Tiramisu Coffee Bean', 'media/186-dp184-tiramisu-coffee-bean-sm.jpg'],
    ['Orange Cranberry Bread Butter Pudding', 'media/186-dp186-orange-cranberry-bread-butter-pudding-sm.jpg'],
    ['Sticky Toffee Pudding 70mm', 'media/186-dp187-sticky-toffee-pudding-70mm-sm.jpg'],
    ['Lemon Meringue Tart 70mm', 'media/186-dp185-lemon-meringue-tart-70mm-sm.jpg'],
    ['Apple Tart Tatin 70mm', 'media/186-dp155-apple-tart-tatin-70mm-sm.jpg'],
    ['Lemon Tart', 'media/186-dp003-lemon-tart-sm.jpg'],
    ['Vegan Marbled Chocolate Slice', 'media/186-dp027-vegan-marbled-chocolate-slice-sm.jpg'],
    ['Carrot Cake with Walnuts 70mm', 'media/186-dp145-carrot-cake-with-walnuts-70mm-sm.jpg'],
    ['Rhubarb Clafouti Tart', 'media/186-dp152-rhubarb-clafouti-tart-sm.jpg'],
    ['Cherry Clafouti Tart', 'media/186-dp024-cherry-clafouti-tart-sm.jpg'],
    ['Ginger Cake Slice', 'media/186-dp070-ginger-cake-slice-sm.jpg'],
    ['Dark Chocolate Delice', 'media/186-dp017-dark-chocolate-delice-sm.jpg'],
    ['Passion Fruit Slice', 'media/186-dp018-passion-fruit-slice-sm.jpg'],
    ['Passion Fruit Slice 6x3cm', 'media/186-dp068-passion-fruit-slice-6x3cm-sm.jpg'],
    ['Chocolate Fudge Gateau', 'media/186-dp069-chocolate-fudge-gateau-sm.jpg'],
    ['Raspberry Cream Slice', 'media/186-dp071-raspberry-cream-slice-sm.jpg'],
    ['Vanilla Millefeuille', 'media/186-dp157-vanilla-millefeuille-sm.jpg'],
    ['Classic Chocolate \u00c9clair', 'media/186-dp158-classic-chocolate-eclair-sm.jpg'],
    ['Classic Salted Caramel \u00c9clair', 'media/186-dp159-classic-salted-caramel-eclair-sm.jpg'],
    ['Creme Catalan \u00c9clair', 'media/186-dp162-creme-catalan-eclair-sm.jpg'],
    ['Carrot Cake Slice', 'media/186-dp161-carrot-cake-slice-sm.jpg'],
    ['Paris Brest', 'media/186-dp160-paris-brest-sm.jpg'],
    ['Chocolate Fudge Cake', 'media/186-dp146-chocolate-fudge-cake-sm.jpg'],
    ['Victoria Sponge Cake', 'media/186-dp147-victoria-sponge-cake-sm.jpg'],
    ['Chocolate Fudge Tart', 'media/186-dp001-chocolate-fudge-tart-sm.jpg'],
    ['Apricot Almond Tart', 'media/186-dp007-apricot-almond-tart-sm.jpg'],
    ['Lemon Meringue Tart', 'media/186-dp004-lemon-meringue-tart-sm.jpg'],
    ['Raspberry Tart', 'media/186-dp008-raspberry-tart-sm.jpg'],
    ['Traditional Canel\u00e9', 'media/186-dp154-traditional-canele-sm.jpg'],
    ['Lemon Posset \u00c9clair', 'media/186-dp135-lemon-posset-eclair-sm.jpg'],
    ['Tart Tatin', 'media/186-dp010-tart-tatin-sm.jpg'],
    ['Individual Chocolate \u00c9clair', 'media/186-dp089-individual-chocolate-eclair-sm.jpg'],
    ['Individual Salted Caramel \u00c9clair', 'media/186-dp090-individual-salted-caramel-eclair-sm.jpg'],
  ],
};

const deriveCode = (path: string, fallbackIndex: number): string => {
  const m = path.match(/(dp\d+)/i);
  return m ? m[1].toUpperCase() : `DP${900 + fallbackIndex}`;
};

/** Flattened product list seeded from the design. */
export const PRODUCTS: Product[] = Object.entries(SEED_ITEMS).flatMap(([key, items]) =>
  items.map(([name, path], i) => ({
    code: deriveCode(path, i),
    name,
    rangeKey: key,
    image: mediaUrl(path),
  })),
);

export const getRanges = (kind: RangeKind): Range[] => RANGES.filter((r) => r.kind === kind);

export const getRange = (kind: RangeKind, slug: string): Range | undefined =>
  RANGES.find((r) => r.kind === kind && r.slug === slug);

export const getRangeByKey = (key: string): Range | undefined => RANGES.find((r) => r.key === key);

export const getProductsForRange = (key: string): Product[] => PRODUCTS.filter((p) => p.rangeKey === key);

export const getProductByCode = (code: string): Product | undefined =>
  PRODUCTS.find((p) => p.code.toLowerCase() === code.toLowerCase());

/** Derive a human format/size token from a product name (e.g. "70mm", "9x3cm"). */
export const deriveFormat = (name: string): string | null => {
  const m = name.match(/(\d+\s?mm|\d+\s?x\s?\d+\s?cm)/i);
  return m ? m[1].replace(/\s+/g, '') : null;
};
